import React, { useEffect, useMemo } from 'react'
import DropDown, { IDropDownData } from '../DropDown/DropDown'
import type { IDropDownProps } from '../DropDown/DropDown'
import { CREATE_NEW, CSP_FIELD_NAME } from '../GlobalConstants'
import { useFormContext } from 'react-hook-form'
import { ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP, ACTIVATION_UI_ERROR_CODES } from '../../../ErrorCodes'
import type { LockedInfo } from '../../../store'
import type { SupportAccount, Tsg } from '@sparky/framework'

const fieldName = CSP_FIELD_NAME

export type ICspDropDown = Partial<Omit<IDropDownProps, 'fieldName'>> & {
  /**
   * pass this as undefined will not use the existing instance inside TSG to pick csp account,
   * currently used for SDWAN standalone only
   * normally should pass the tsg instance csp account
   */
  curTsgInstCsp: string | undefined
  isLoading: boolean
  tsg: Tsg | undefined
  currentTsg: string
  defaultCSP?: IDropDownData
  lockedInfo?: LockedInfo
  callbacks: IDropDownProps['callbacks']
  supportAccounts: SupportAccount[],
  disable?: boolean,
}

const defaultProps = {
  display: {
    errorMessage: 'Select Customer support Account to proceed activation',
    toolTipMessage: 'Select the Customer support account where the license is to be activated',
  },
  dropDownProps: {
    required: true,
    triggerValidationOnChange: true,
  },
  size: {
    height: 32,
    width: 300
  },
  passByProps: {
    dataTestId: 'csp-activation-screen',
    enableSearch: true,
    label: 'Customer Support Account',
    placeholder: 'Select Customer Support Account',
  }
}

const CSPDropDown = (props: ICspDropDown) => {
  const { curTsgInstCsp, isLoading, currentTsg, defaultCSP, supportAccounts = [], lockedInfo, disable } = props
  const { setValue } = useFormContext()
  const { clearErrors, setError } = useFormContext()
  const supportAccountList: IDropDownData[] = useMemo(() => {
    const filteredList: SupportAccount[] = []
    if (!isLoading) {
      // if curTsgInstCsp is undefined, render the whole csp list
      if ((currentTsg === '' || currentTsg === CREATE_NEW || !curTsgInstCsp) && !lockedInfo?.csp_id) {
        filteredList.push(...supportAccounts)
      }
      else {
        // if lockedInfo or instance is present, that csp takes priority
        const support_account_id = lockedInfo?.csp_id || curTsgInstCsp
        support_account_id && filteredList.push(...supportAccounts.filter((each: any) => +each.accountid === +support_account_id))
      }
      return filteredList?.map((each: any) => {
        return {
          value: `${each.accountid} - ${each.name}`,
          description: each.name,
          id: each.accountid,
          children: `${each.accountid} - ${each.name}`
        }
      })
    }
    else {
      return []
    }
  }, [isLoading, currentTsg, curTsgInstCsp, lockedInfo?.csp_id, supportAccounts])
  useEffect(() => {
    // auto select if there's only one in supportAccount
    if (supportAccountList?.length === 1) {
      setValue(fieldName, supportAccountList[0])
      clearErrors(fieldName)

    }
    if ((!isLoading && curTsgInstCsp) || lockedInfo?.csp_id) {
      if (supportAccountList?.length) {
        supportAccountList?.length && setValue(fieldName, supportAccountList[0])
        clearErrors(fieldName)
      }
      else if (supportAccountList?.length === 0) {
        setValue(fieldName, '')
        setError(fieldName, { message: ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.NO_ACCESS_TO_CSP] })
      }
    }
    if (defaultCSP) {
      setValue(fieldName, defaultCSP)
    }
    if (!isLoading && !curTsgInstCsp) {
      clearErrors(fieldName)
    }
  }, [clearErrors, curTsgInstCsp, isLoading, setError, setValue, supportAccountList, supportAccountList?.length, defaultCSP, lockedInfo])
  useEffect(() => {
    if (currentTsg === CREATE_NEW && !lockedInfo?.csp_id) {
      setValue(fieldName, '')
    }
  }, [currentTsg, lockedInfo, setValue])

  return <div>
    <DropDown{...{
      ...props, dropDownList: supportAccountList, isLoading, defaultValue: defaultCSP,
      disabled: disable,
      fieldName,
      display: {
        ...defaultProps.display,
        ...props.display
      },
      dropDownProps: {
        ...defaultProps.dropDownProps,
        ...props.dropDownProps,
      },
      passByProps: {
        ...defaultProps.passByProps,
        ...props.passByProps,
      },
      size: {
        ...defaultProps.size,
        ...props.size
      }
    }} />
  </div>
}

export default CSPDropDown
